import { useStore } from '@/store'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import FilterModelPro, { GridFilterModelPro } from '../utils/filterModelPro'
import { resolverFiltroInicial } from '../utils/filtroPersistido'

export const filtroVazio: GridFilterModelPro = {
    items: [] as GridFilterModelPro['items'],
    quickFilterValue: '',
}

/**
 * Estado do filtro da tabela, persistido por `tableId` (localStorage).
 * `tableId` undefined desliga a persistência. Só o DataTable passa undefined,
 * quando o filtro é controlado pela tela.
 *
 * @returns `[filterModel, setFilterModel, filtroProps]` — `filtroProps` vai em
 * spread no `DataTable`, para `tableId` e padrão serem declarados só aqui.
 */
const useDefaultFilterModel = (
    tableId: string | undefined,
    filtroPadrao: GridFilterModelPro = filtroVazio,
    dontRedirect = false,
) => {
    const [searchParams] = useSearchParams()
    const urlFilter = searchParams.get('grid-filter')
    const salvo = useStore(state =>
        tableId ? state.dataTable.filtros[tableId] : undefined,
    )
    const setFiltro = useStore(state => state.dataTable.setFiltro)

    let daUrl: GridFilterModelPro['items'] | undefined
    if (urlFilter && !dontRedirect) {
        daUrl = FilterModelPro.fromUrlParams(urlFilter).filterModel.items
        searchParams.delete('grid-filter')
        window.history.replaceState(
            {},
            '',
            `${window.location.pathname}?${searchParams}`,
        )
    }

    function montarFiltro(
        daUrl: GridFilterModelPro['items'] | undefined,
    ): GridFilterModelPro {
        // Sem id, excluir/editar um item do filtro padrão casa todos os itens
        // (undefined === undefined) e afeta o filtro inteiro.
        const items = resolverFiltroInicial(
            daUrl,
            salvo,
            filtroPadrao.items || [],
        ).map(item => ({ ...item }))
        for (const item of items)
            if (item.id === undefined) item.id = FilterModelPro.gerarID(items)

        return { items, quickFilterValue: filtroPadrao.quickFilterValue || '' }
    }

    const [filterModel, setFilterModel] = useState(() => montarFiltro(daUrl))

    // Ajustado no render, não num efeito: o efeito de gravação nunca pode ver o
    // `tableId` novo com os itens da tabela anterior.
    const [tableIdFiltro, setTableIdFiltro] = useState(tableId)
    if (tableId !== tableIdFiltro) {
        setTableIdFiltro(tableId)
        setFilterModel(montarFiltro(undefined))
    }

    useEffect(() => {
        if (tableId) setFiltro(tableId, filterModel.items)
    }, [tableId, filterModel.items, setFiltro])

    const filtroProps = { tableId, filtroPadrao, filterModel }

    return [filterModel, setFilterModel, filtroProps] as const
}

export default useDefaultFilterModel
